import { ResultsTableSortableColumns } from "@/components/table/views/job/job-columns";
import { CompetencyFit } from "@/types/screeners";

export type AcceptedValue = string | number | boolean | Date;
type ParamValue = AcceptedValue | AcceptedValue[];

// Using object allows interfaces without explicit index signatures to satisfy the constraint
// while still allowing properly typed access via T[K] for known keys
export type ParamsConstraint = object;

// Validates that all properties of T are valid ParamValue types at compile time
export type AssertValidParams<T> = {
  [K in keyof T]: T[K] extends ParamValue ? T[K] : never;
};

export type ArrayKeys<T> = {
  [K in keyof T]: T[K] extends AcceptedValue[] ? K : never;
}[keyof T];

export type ElementOf<T> = T extends (infer U)[] ? U : never;

export interface FilterParamsReadable<T extends ParamsConstraint> {
  get<K extends keyof T>(key: K): T[K] | null;
  getAll<K extends ArrayKeys<T>>(key: K): T[K] | null;
  getWithDefault<K extends keyof T>(key: K, defaultValue: T[K]): T[K];
  has<K extends keyof T>(key: K): boolean;
  has<K extends ArrayKeys<T>>(key: K, value: ElementOf<T[K]>): boolean;
  hasAnyOf<K extends keyof T>(keys: readonly K[]): boolean;
  toString(): string;
  toObject(): Partial<T>;
  keys(): IterableIterator<keyof T>;
  values(): IterableIterator<T[keyof T]>;
  entries(): IterableIterator<[keyof T, T[keyof T]]>;
  readonly size: number;
}

const stringify = (value: AcceptedValue): string =>
  value instanceof Date ? value.toISOString() : String(value);

export class FilterParams<
  T extends ParamsConstraint,
> implements FilterParamsReadable<T> {
  private readonly data: Map<keyof T, T[keyof T]>;
  private readonly initialData: Map<keyof T, T[keyof T]>;

  constructor(initial?: Partial<T & AssertValidParams<T>> | FilterParams<T>) {
    if (initial instanceof FilterParams) {
      this.data = new Map(initial.data);
      this.initialData = new Map(initial.initialData);
    } else if (initial) {
      this.data = new Map(
        Object.entries(initial).filter(
          ([, value]) =>
            value !== undefined &&
            !(Array.isArray(value) && value.length === 0),
        ) as [keyof T, T[keyof T]][],
      );
      this.initialData = new Map(this.data);
    } else {
      this.data = new Map();
      this.initialData = new Map();
    }
  }

  clear(): FilterParams<T> {
    const next = new FilterParams(this);
    next.data.clear();
    this.initialData.forEach((value, key) => next.data.set(key, value));
    return next;
  }

  get<K extends keyof T>(key: K): T[K] | null {
    return (this.data.get(key) as T[K]) ?? null;
  }

  getAll<K extends ArrayKeys<T>>(key: K): T[K] | null {
    return this.get(key);
  }

  getWithDefault<K extends keyof T>(key: K, defaultValue: T[K]): T[K] {
    return this.get(key) ?? defaultValue;
  }

  set<K extends keyof T>(key: K, value: T[K]): FilterParams<T> {
    const next = new FilterParams(this);

    if (Array.isArray(value) && value.length === 0) {
      next.data.delete(key);
    } else {
      next.data.set(key, value);
    }

    return next;
  }

  append<K extends ArrayKeys<T>>(
    key: K,
    value: ElementOf<T[K]>,
  ): FilterParams<T> {
    const current = this.data.get(key);
    const currentArray = Array.isArray(current) ? current : [];
    const nextArray = [...currentArray, value] as T[K];

    return this.set(key, nextArray);
  }

  delete<K extends keyof T>(key: K): FilterParams<T>;
  delete<K extends ArrayKeys<T>>(
    key: K,
    value: ElementOf<T[K]>,
  ): FilterParams<T>;
  delete<K extends keyof T>(key: K, value?: unknown): FilterParams<T> {
    const next = new FilterParams(this);

    if (value === undefined) {
      next.data.delete(key);
    } else {
      const current = next.data.get(key);

      if (Array.isArray(current)) {
        const filtered = current.filter((v) => v !== value);

        if (filtered.length === 0) {
          next.data.delete(key);
        } else {
          next.data.set(key, filtered as T[K]);
        }
      }
    }

    return next;
  }

  has<K extends keyof T>(key: K): boolean;
  has<K extends ArrayKeys<T>>(key: K, value: ElementOf<T[K]>): boolean;
  has<K extends keyof T>(key: K, value?: unknown): boolean {
    const current = this.data.get(key);

    if (current === undefined) {
      return false;
    }

    if (value === undefined) {
      return true;
    }

    return Array.isArray(current) && current.includes(value);
  }

  hasAnyOf<K extends keyof T>(keys: readonly K[]): boolean {
    return keys.some((key) => this.has(key));
  }

  toggleValue<K extends ArrayKeys<T>>(
    key: K,
    value: ElementOf<T[K]>,
  ): FilterParams<T> {
    const current = this.data.get(key);

    if (Array.isArray(current) && current.includes(value)) {
      return this.delete(key, value);
    }

    return this.append(key, value);
  }

  replaceAll(newState: Partial<T & AssertValidParams<T>>): FilterParams<T> {
    const next = new FilterParams(this);
    next.data.clear();
    this.initialData.forEach((value, key) => next.data.set(key, value));
    Object.entries(newState).forEach(([key, value]) => {
      if (
        value !== undefined &&
        !(Array.isArray(value) && value.length === 0)
      ) {
        next.data.set(key as keyof T, value as T[keyof T]);
      }
    });
    return next;
  }

  keys(): IterableIterator<keyof T> {
    return this.data.keys();
  }

  values(): IterableIterator<T[keyof T]> {
    return this.data.values();
  }

  entries(): IterableIterator<[keyof T, T[keyof T]]> {
    return this.data.entries();
  }

  get size(): number {
    return this.data.size;
  }

  toObject(): Partial<T> {
    return Object.fromEntries(this.data) as Partial<T>;
  }

  toString(): string {
    const params = new URLSearchParams();

    this.data.forEach((value, key) => {
      const stringKey = String(key);

      // Transform sortBy value to strip "competencies." or "showInSummary." prefix
      // since the backend expects just the field name without the prefix
      if (stringKey === "sortBy" && typeof value === "string") {
        const sortValue = value.startsWith("competencies.")
          ? value.slice("competencies.".length)
          : value.startsWith("showInSummary.")
            ? value.slice("showInSummary.".length)
            : value;
        params.set(stringKey, sortValue);
      } else if (Array.isArray(value)) {
        value.forEach((v) =>
          params.append(stringKey, stringify(v as AcceptedValue)),
        );
        // BE's competency fit enum matches ours, but without spaces. Currently,
        // this only matters for "Very High" as the other options have no spaces
      } else if (
        (stringKey.startsWith("competencies.") ||
          stringKey === ResultsTableSortableColumns.OverallFit) &&
        value === CompetencyFit.VeryHigh
      ) {
        params.set(stringKey, "VeryHigh");
      } else if (value !== undefined && value !== null) {
        params.set(stringKey, stringify(value as AcceptedValue));
      }
    });

    params.sort();

    return params.toString();
  }
}
